import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { sortDocumentsByName } from "helpers/customPageDocuments";
import { fileBadge, formatSize } from "helpers/helper_funcations";
import { get } from "helpers/api_helper";
import DownloadAllImagesButton from "./DownloadAllImagesButton";

const CustomPageResourcesTable = ({ documents = [], showDownloadAll = false }) => {
  const [selectedDoc, setSelectedDoc] = useState(null);

  const sortedDocuments = useMemo(
    () => sortDocumentsByName(documents),
    [documents]
  );

  const fetchSecureUrl = async ({ download = true, id } = {}) => {
    if (!id) return null;
    try {
      const qs = download ? "?download=true" : "";
      const json = await get(`/documents/${id}/secure-url${qs}`);
      if (!json?.success) throw new Error(json?.message || "Failed to fetch document");
      return json.data?.signedUrl || null;
    } catch (e) {
      console.error("Failed to load document:", e);
      return null;
    }
  };

  const handleDownload = async (doc) => {
    if (!doc || selectedDoc?.id === doc.id) return;
    setSelectedDoc(doc);

    let url = null;
    try {
      url = await fetchSecureUrl({ download: true, id: doc.id });
      if (!url) return;

      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || doc.name || "file";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        console.error("Download failed:", e);
      }
    } finally {
      setSelectedDoc(null);
    }
  };

  if (!sortedDocuments.length) return null;

  return (
    <div className="custom-page-resources mt-4">
      {showDownloadAll ? <DownloadAllImagesButton documents={sortedDocuments} /> : null}
      <Card className="custom-page-resources-table mb-0">
        <CardBody>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Resource Name</th>
                <th>File</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocuments.map((doc) => {
                const { id, name, fileName, mimeType, size } = doc;
                const downloading = selectedDoc?.id === id;
                return (
                  <tr key={id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {fileBadge(mimeType, fileName)}
                        <span className="fw-semibold" data-testid="custom-page-resource-name">
                          {name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div>{fileName}</div>
                      <small className="text-muted">{formatSize(size)}</small>
                    </td>
                    <td>
                      <div className="flex-row">
                        <Link
                          className="download-rounded-btn"
                          to={`/view-document/${id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`View ${name}`}
                        >
                          <i className="mdi mdi-eye" style={{ color: "white", fontSize: 15 }} />
                        </Link>
                        <button
                          type="button"
                          className="download-rounded-btn"
                          onClick={() => handleDownload(doc)}
                          disabled={downloading}
                          aria-label={`Download ${name}`}
                          title={downloading ? "Downloading…" : `Download ${name}`}
                          style={{ border: "none" }}
                        >
                          {downloading ? (
                            <i className="mdi mdi-loading mdi-spin" style={{ color: "white", fontSize: 15 }} />
                          ) : (
                            <i className="mdi mdi-download" style={{ color: "white", fontSize: 15 }} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </CardBody>
      </Card>
    </div>
  );
};

CustomPageResourcesTable.propTypes = {
  showDownloadAll: PropTypes.bool,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      fileName: PropTypes.string,
      mimeType: PropTypes.string,
      size: PropTypes.number,
    })
  ),
};

export default CustomPageResourcesTable;
